import { X, Bell, CheckCheck, Zap, Newspaper, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";

export const NotificationPanel = ({ notifications, onClose, onRefresh }) => {
  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      toast.success("All notifications marked as read");
      onRefresh();
    } catch (error) {
      console.error("Error marking all read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      onRefresh();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case "price_alert":
        return <Zap className="w-4 h-4 text-warning" />;
      case "news":
        return <Newspaper className="w-4 h-4 text-info" />;
      case "market_event":
        return <TrendingUp className="w-4 h-4 text-profit" />;
      default:
        return <Bell className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "critical":
        return "border-l-loss";
      case "high":
        return "border-l-warning";
      default:
        return "border-l-border";
    }
  };

  return (
    <div 
      className="absolute right-0 top-12 w-80 md:w-96 bg-card border border-border shadow-2xl z-50 animate-slide-in"
      data-testid="notification-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-heading font-bold">NOTIFICATIONS</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllRead}
            className="text-xs gap-1"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-3 h-3" />
            Mark all read
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="w-8 h-8"
            data-testid="close-notifications-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      <ScrollArea className="h-96">
        <div className="p-2">
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <div
                key={notification.id}
                className={`
                  p-3 mb-2 border-l-2 cursor-pointer transition-colors
                  ${notification.is_read ? "bg-background/50" : "bg-background"}
                  ${getPriorityClass(notification.priority)}
                  hover:bg-muted/50
                `}
                onClick={() => handleMarkRead(notification.id)}
                style={{ animationDelay: `${index * 30}ms` }}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 mt-0.5">
                    {getIcon(notification.notification_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className={`font-heading font-bold text-sm ${notification.is_read ? "text-muted-foreground" : "text-white"}`}>
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-profit rounded-full shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(notification.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <Bell className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">
                You're all caught up!
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
